import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApi } from '../api-context.tsx'
import { eventKeys } from './useEvents.ts'

/**
 * An event's picture and its files and links. Uploads take the usual three
 * steps — a slot, the bytes straight to the bucket, then a confirm — so the
 * API never carries the file and a slow phone cannot time out a request
 * doing database work.
 */

export function useEventFiles(eventId: string, enabled = true) {
  const api = useApi()
  return useQuery({
    queryKey: eventKeys.files(eventId),
    enabled: enabled && eventId !== '',
    queryFn: async () => {
      const { data, error } = await api.api.v1.events({ id: eventId }).files.get()
      if (error) throw error
      return data
    },
  })
}

export type EventFile = NonNullable<ReturnType<typeof useEventFiles>['data']>[number]

/** Where an event file's bytes are served: proxied, so the session applies. */
export const eventFileUrl = (eventId: string, fileId: string) =>
  `/api/v1/events/${eventId}/files/${fileId}/download`

/** Where an event's picture is served; the version busts the cache on a new one. */
export const eventImageUrl = (eventId: string, version?: string | null) =>
  `/api/v1/events/${eventId}/image${version ? `?v=${encodeURIComponent(version)}` : ''}`

export const EVENT_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const
type EventImageType = (typeof EVENT_IMAGE_TYPES)[number]
const isImageType = (type: string): type is EventImageType =>
  (EVENT_IMAGE_TYPES as readonly string[]).includes(type)

async function putToBucket(url: string, file: Blob, contentType: string) {
  // Straight to the bucket with fetch: another origin with its own signature,
  // and no reason to send it our session.
  const uploaded = await fetch(url, {
    method: 'PUT',
    headers: { 'content-type': contentType },
    body: file,
  })
  if (!uploaded.ok) throw new Error(`The file did not reach storage (${uploaded.status}).`)
}

export function useEventFileMutations(eventId: string) {
  const api = useApi()
  const queryClient = useQueryClient()
  const invalidate = () => queryClient.invalidateQueries({ queryKey: eventKeys.files(eventId) })
  const invalidateEvents = () => queryClient.invalidateQueries({ queryKey: eventKeys.all() })

  const addLink = useMutation({
    mutationFn: async (body: { title: string; url: string }) => {
      const { data, error } = await api.api.v1.events({ id: eventId }).files.link.post(body)
      if (error) throw error
      return data
    },
    onSuccess: () => void invalidate(),
  })

  const upload = useMutation({
    mutationFn: async (input: { title: string; file: File }) => {
      const contentType = input.file.type || 'application/octet-stream'
      const slot = await api.api.v1.events({ id: eventId }).files['upload-url'].post({
        contentType,
        sizeBytes: input.file.size,
      })
      if (slot.error) throw slot.error
      await putToBucket(slot.data.url, input.file, contentType)
      const { data, error } = await api.api.v1.events({ id: eventId }).files.file.post({
        title: input.title,
        key: slot.data.key,
        fileName: input.file.name,
      })
      if (error) throw error
      return data
    },
    onSuccess: () => void invalidate(),
  })

  const remove = useMutation({
    mutationFn: async (fileId: string) => {
      const { error } = await api.api.v1.events({ id: eventId }).files({ fileId }).delete()
      if (error) throw error
    },
    onSuccess: () => void invalidate(),
  })

  const setImage = useMutation({
    mutationFn: async (file: File) => {
      if (!isImageType(file.type)) throw new Error('A picture must be JPEG, PNG or WebP.')
      const slot = await api.api.v1.events({ id: eventId }).image['upload-url'].post({
        contentType: file.type,
        sizeBytes: file.size,
      })
      if (slot.error) throw slot.error
      await putToBucket(slot.data.url, file, file.type)
      const { data, error } = await api.api.v1.events({ id: eventId }).image.put({
        key: slot.data.key,
      })
      if (error) throw error
      return data
    },
    onSuccess: () => void invalidateEvents(),
  })

  const removeImage = useMutation({
    mutationFn: async () => {
      const { data, error } = await api.api.v1.events({ id: eventId }).image.delete()
      if (error) throw error
      return data
    },
    onSuccess: () => void invalidateEvents(),
  })

  return { addLink, upload, remove, setImage, removeImage }
}
